import {
  brDateToIso,
  isEmail,
  isValidCpf,
  maskCpf,
  maskPostalCode,
  maskWhatsapp,
  onlyDigits,
} from '@/lib/masks'
import type { Prefill } from '../identity/api'
import type { DocumentValues } from '../identity/document'
import type {
  ForeignDocumentType,
  Gender,
  MaritalStatus,
  ReferralChannel,
  RegisterInput,
} from './api'
import {
  countryName,
  DEFAULT_DIAL_CODE,
  DIAL_CODES,
  FOREIGN_DOCUMENT_TYPE_LABELS,
  GENDER_LABELS,
  MARITAL_STATUS_LABELS,
} from './labels'

/**
 * O formulário do cadastro pelo link (docs/areas/pacientes.md RN-PAC-33 e 42): os campos ficam como a
 * pessoa digita (com máscara) e o corpo de `POST /registration` sai de `toRegisterInput`. Só nome,
 * WhatsApp e o aceite impedem enviar; o resto, quando começado, precisa estar inteiro (o endereço e
 * o documento estrangeiro). As mensagens são as da API.
 */

/** As etapas do fluxo (T52). "Responsável legal" só existe para menor de idade ou quando marcado. */
export type StepId = 'you' | 'contact' | 'guardian' | 'review'

export const STEP_TITLES: Record<StepId, string> = {
  you: 'Seus dados',
  contact: 'Contato e endereço',
  guardian: 'Responsável legal',
  review: 'Conferir e enviar',
}

export interface RegisterValues {
  name: string
  birthDate: string
  docNumber: string
  /** Sem CPF: o documento estrangeiro entra no lugar (RN-PAC-04). */
  hasNoCpf: boolean
  foreignType: ForeignDocumentType
  foreignNumber: string
  foreignCountry: string
  rg: string
  maritalStatus: MaritalStatus | ''
  occupation: string
  gender: Gender | ''
  dialCode: string
  whatsapp: string
  email: string
  instagram: string
  country: string
  postalCode: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  /** Maior de idade com responsável legal (tutela, curatela; RT-18). Menor sempre tem. */
  hasGuardian: boolean
  guardianName: string
  guardianDocNumber: string
  guardianWhatsapp: string
  referralChannel: ReferralChannel | ''
  referralDetail: string
}

export type FieldKey = keyof RegisterValues | 'privacyAccepted'
export type FieldErrors = Partial<Record<FieldKey, string>>

export const EMPTY_VALUES: RegisterValues = {
  name: '',
  birthDate: '',
  docNumber: '',
  hasNoCpf: false,
  foreignType: 'passport',
  foreignNumber: '',
  foreignCountry: '',
  rg: '',
  maritalStatus: '',
  occupation: '',
  gender: '',
  dialCode: DEFAULT_DIAL_CODE,
  whatsapp: '',
  email: '',
  instagram: '',
  country: 'BR',
  postalCode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  hasGuardian: false,
  guardianName: '',
  guardianDocNumber: '',
  guardianWhatsapp: '',
  referralChannel: '',
  referralDetail: '',
}

export const MESSAGES = {
  name: 'Informe o nome completo.',
  nameShort: 'O nome precisa de pelo menos 2 letras.',
  nameLong: 'O nome pode ter no máximo 120 caracteres.',
  nameDigits: 'O nome não pode ter números.',
  birthDate: 'Use o formato dd/mm/aaaa.',
  birthDateFuture: 'A data de nascimento não pode ser no futuro.',
  birthYear: 'Confira o ano de nascimento.',
  cpf: 'O CPF precisa de 11 dígitos.',
  cpfInvalid: 'Esse CPF não é válido. Confira os dois últimos dígitos.',
  foreignNumberRequired: 'Informe o número do documento.',
  foreignNumber: 'O documento pode ter de 4 a 20 letras e números.',
  foreignCountry: 'Escolha o país que emitiu o documento.',
  rg: 'O RG pode ter de 5 a 20 letras e números.',
  occupation: 'A profissão precisa de pelo menos 2 letras.',
  whatsappRequired: 'Informe o telefone com DDD.',
  whatsapp: 'Informe o DDD e o número.',
  email: 'Confira o e-mail: precisa ter @ e um domínio, como nome@exemplo.com.br.',
  instagram: 'Use só o nome de usuário, sem @.',
  postalCode: 'O CEP precisa de 8 dígitos.',
  foreignPostalCode: 'Informe o código postal, com até 20 caracteres.',
  street: 'Informe a rua.',
  number: 'Informe o número, ou s/n.',
  district: 'Informe o bairro.',
  city: 'Informe a cidade.',
  state: 'Escolha a UF.',
  foreignState: 'Informe o estado ou a província, com até 60 caracteres.',
  guardianName: 'Informe o nome do responsável.',
  guardianDocNumber: 'O CPF do responsável precisa de 11 dígitos.',
  guardianDocNumberInvalid: 'Esse CPF não é válido. Confira os dois últimos dígitos.',
  guardianWhatsapp: 'Informe o telefone do responsável com DDD.',
  privacy: 'Para enviar, aceite a nota de privacidade.',
} as const

/** Hoje no aparelho, `aaaa-mm-dd`. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Menor de 18 anos pela data digitada: só adianta a etapa; quem decide é a API, no fuso da clínica. */
export function isMinorOn(birthDate: string, today: string): boolean {
  const iso = brDateToIso(birthDate)
  if (!iso || iso > today) return false
  return today < `${Number(iso.slice(0, 4)) + 18}${iso.slice(4)}`
}

export function needsGuardian(values: RegisterValues, today: string): boolean {
  return values.hasGuardian || isMinorOn(values.birthDate, today)
}

export function stepsFor(values: RegisterValues, today: string): StepId[] {
  return needsGuardian(values, today)
    ? ['you', 'contact', 'guardian', 'review']
    : ['you', 'contact', 'review']
}

const ADDRESS_KEYS = [
  'postalCode',
  'street',
  'number',
  'complement',
  'district',
  'city',
  'state',
] as const

/** O endereço é opcional, mas começado precisa estar inteiro (como na API). */
export function hasAddress(values: RegisterValues): boolean {
  return ADDRESS_KEYS.some((key) => values[key].trim() !== '')
}

const isBrazil = (values: RegisterValues) => values.country === 'BR'

function phoneError(digits: string, dialCode: string, empty: string, invalid: string) {
  if (digits.length === 0) return empty
  const ok =
    dialCode === DEFAULT_DIAL_CODE
      ? digits.length >= 10 && digits.length <= 11
      : digits.length >= 10 && digits.length <= 13
  return ok ? undefined : invalid
}

function nameError(text: string, required: string): string | undefined {
  const name = text.trim()
  if (name === '') return required
  if (/\d/.test(name)) return MESSAGES.nameDigits
  if (name.length < 2) return MESSAGES.nameShort
  if (name.length > 120) return MESSAGES.nameLong
  return undefined
}

function cpfError(text: string, short: string, invalid: string): string | undefined {
  const digits = onlyDigits(text)
  if (digits.length !== 11) return short
  return isValidCpf(digits) ? undefined : invalid
}

/** Os erros da etapa, campo a campo, com as mensagens da API. */
export function validateStep(step: StepId, values: RegisterValues, today: string): FieldErrors {
  const errors: FieldErrors = {}
  const set = (key: FieldKey, message: string | undefined) => {
    if (message) errors[key] = message
  }
  if (step === 'you') {
    set('name', nameError(values.name, MESSAGES.name))
    if (values.birthDate.trim()) {
      const iso = brDateToIso(values.birthDate)
      set(
        'birthDate',
        !iso
          ? MESSAGES.birthDate
          : iso > today
            ? MESSAGES.birthDateFuture
            : iso < '1900-01-01'
              ? MESSAGES.birthYear
              : undefined,
      )
    }
    if (values.hasNoCpf) {
      const number = values.foreignNumber.trim()
      set(
        'foreignNumber',
        number === ''
          ? MESSAGES.foreignNumberRequired
          : /^[A-Za-z0-9]{4,20}$/.test(number)
            ? undefined
            : MESSAGES.foreignNumber,
      )
      set('foreignCountry', values.foreignCountry ? undefined : MESSAGES.foreignCountry)
    } else if (values.docNumber.trim()) {
      set('docNumber', cpfError(values.docNumber, MESSAGES.cpf, MESSAGES.cpfInvalid))
    }
    const rg = values.rg.replace(/[.\-/\s]/g, '')
    if (rg !== '' && !/^[A-Za-z0-9]{5,20}$/.test(rg)) set('rg', MESSAGES.rg)
    const occupation = values.occupation.trim()
    if (occupation !== '' && occupation.length < 2) set('occupation', MESSAGES.occupation)
  }
  if (step === 'contact') {
    set(
      'whatsapp',
      phoneError(
        onlyDigits(values.whatsapp),
        values.dialCode,
        MESSAGES.whatsappRequired,
        MESSAGES.whatsapp,
      ),
    )
    if (values.email.trim() && !isEmail(values.email)) set('email', MESSAGES.email)
    if (values.instagram && !/^[A-Za-z0-9._]{1,30}$/.test(values.instagram)) {
      set('instagram', MESSAGES.instagram)
    }
    if (hasAddress(values)) {
      // Na ordem dos campos da tela: o foco vai para o primeiro com erro.
      const postal = values.postalCode.trim()
      const brazil = isBrazil(values)
      if (brazil ? onlyDigits(postal).length !== 8 : postal.length < 1 || postal.length > 20) {
        set('postalCode', brazil ? MESSAGES.postalCode : MESSAGES.foreignPostalCode)
      }
      if (values.street.trim().length < 2) set('street', MESSAGES.street)
      if (values.number.trim() === '') set('number', MESSAGES.number)
      if (values.district.trim().length < 2) set('district', MESSAGES.district)
      if (values.city.trim().length < 2) set('city', MESSAGES.city)
      const state = values.state.trim()
      if (brazil ? state === '' : state.length < 1 || state.length > 60) {
        set('state', brazil ? MESSAGES.state : MESSAGES.foreignState)
      }
    }
  }
  if (step === 'guardian') {
    set(
      'guardianName',
      nameError(values.guardianName, MESSAGES.guardianName) && MESSAGES.guardianName,
    )
    set(
      'guardianDocNumber',
      cpfError(
        values.guardianDocNumber,
        MESSAGES.guardianDocNumber,
        MESSAGES.guardianDocNumberInvalid,
      ),
    )
    set(
      'guardianWhatsapp',
      phoneError(
        onlyDigits(values.guardianWhatsapp),
        DEFAULT_DIAL_CODE,
        MESSAGES.guardianWhatsapp,
        MESSAGES.guardianWhatsapp,
      ),
    )
  }
  return errors
}

/** O corpo de `POST /registration`: só o que foi informado; o vazio não vai. */
export function toRegisterInput(values: RegisterValues, today: string): RegisterInput {
  const text = (value: string) => value.trim() || undefined
  const email = text(values.email)?.toLowerCase()
  const birthDate = brDateToIso(values.birthDate) ?? undefined
  const docNumber = onlyDigits(values.docNumber) || undefined
  const foreignNumber = text(values.foreignNumber)?.toUpperCase()
  const input: RegisterInput = {
    name: values.name.trim(),
    whatsapp: onlyDigits(values.whatsapp),
    ...(values.dialCode !== DEFAULT_DIAL_CODE ? { dialCode: values.dialCode } : {}),
    ...(email ? { email } : {}),
    ...(birthDate ? { birthDate } : {}),
    privacyAccepted: true,
  }
  if (values.hasNoCpf) {
    if (foreignNumber) {
      input.foreignDocument = {
        type: values.foreignType,
        number: foreignNumber,
        country: values.foreignCountry,
      }
    }
  } else if (docNumber) {
    input.docNumber = docNumber
  }
  if (text(values.rg)) input.rg = values.rg.trim()
  if (values.gender) input.gender = values.gender
  if (values.maritalStatus) input.maritalStatus = values.maritalStatus
  if (text(values.occupation)) input.occupation = values.occupation.trim()
  if (hasAddress(values)) {
    input.address = {
      postalCode: isBrazil(values) ? onlyDigits(values.postalCode) : values.postalCode.trim(),
      street: values.street.trim(),
      number: values.number.trim(),
      ...(text(values.complement) ? { complement: values.complement.trim() } : {}),
      district: values.district.trim(),
      city: values.city.trim(),
      state: values.state.trim(),
      country: values.country,
    }
  }
  if (needsGuardian(values, today)) {
    input.guardian = {
      name: values.guardianName.trim(),
      docNumber: onlyDigits(values.guardianDocNumber),
      whatsapp: onlyDigits(values.guardianWhatsapp),
    }
  }
  if (values.instagram) input.instagram = values.instagram
  if (values.referralChannel) {
    input.referralSource = {
      channel: values.referralChannel,
      ...(text(values.referralDetail) ? { detail: values.referralDetail.trim() } : {}),
    }
  }
  return input
}

/** O campo da API (`address.postalCode`, `guardian.name`) como o campo da tela. */
const API_FIELDS: Record<string, FieldKey> = {
  name: 'name',
  birthDate: 'birthDate',
  docNumber: 'docNumber',
  'foreignDocument.type': 'foreignType',
  'foreignDocument.number': 'foreignNumber',
  'foreignDocument.country': 'foreignCountry',
  rg: 'rg',
  gender: 'gender',
  maritalStatus: 'maritalStatus',
  occupation: 'occupation',
  dialCode: 'dialCode',
  whatsapp: 'whatsapp',
  email: 'email',
  instagram: 'instagram',
  'address.country': 'country',
  'address.postalCode': 'postalCode',
  'address.street': 'street',
  'address.number': 'number',
  'address.complement': 'complement',
  'address.district': 'district',
  'address.city': 'city',
  'address.state': 'state',
  'guardian.name': 'guardianName',
  'guardian.docType': 'guardianDocNumber',
  'guardian.docNumber': 'guardianDocNumber',
  'guardian.whatsapp': 'guardianWhatsapp',
  'referralSource.channel': 'referralChannel',
  'referralSource.detail': 'referralDetail',
  privacyAccepted: 'privacyAccepted',
}

const STEP_OF: Partial<Record<FieldKey, StepId>> = {
  dialCode: 'contact',
  whatsapp: 'contact',
  email: 'contact',
  instagram: 'contact',
  country: 'contact',
  postalCode: 'contact',
  street: 'contact',
  number: 'contact',
  complement: 'contact',
  district: 'contact',
  city: 'contact',
  state: 'contact',
  guardianName: 'guardian',
  guardianDocNumber: 'guardian',
  guardianWhatsapp: 'guardian',
  referralChannel: 'review',
  referralDetail: 'review',
  privacyAccepted: 'review',
}

/** Em que etapa mora o campo da tela. */
export function stepOf(key: FieldKey): StepId {
  return STEP_OF[key] ?? 'you'
}

/** Os erros de um 422 da API, nos campos da tela; o que a tela não conhece fica de fora. */
export function fromApiFields(fields: Record<string, string>): FieldErrors {
  const errors: FieldErrors = {}
  for (const [field, message] of Object.entries(fields)) {
    const key = API_FIELDS[field]
    if (key && !errors[key]) errors[key] = message
  }
  return errors
}

/** O que a etapa "Conferir e enviar" mostra de cada etapa anterior, em frases curtas. */
export function summaryOf(
  values: RegisterValues,
  today: string,
): { step: StepId; lines: string[] }[] {
  const t = (value: string) => value.trim()
  const you = [t(values.name)]
  if (t(values.birthDate)) you.push(`Nascimento em ${t(values.birthDate)}`)
  if (values.hasNoCpf && t(values.foreignNumber)) {
    const country = values.foreignCountry ? ` · ${countryName(values.foreignCountry)}` : ''
    you.push(
      `${FOREIGN_DOCUMENT_TYPE_LABELS[values.foreignType]} ${t(values.foreignNumber).toUpperCase()}${country}`,
    )
  } else if (!values.hasNoCpf && t(values.docNumber)) {
    you.push(`CPF ${t(values.docNumber)}`)
  } else {
    you.push('CPF não informado: a clínica pede na recepção, antes do contrato.')
  }
  if (t(values.rg)) you.push(`RG ${t(values.rg)}`)
  if (values.maritalStatus) you.push(MARITAL_STATUS_LABELS[values.maritalStatus])
  if (t(values.occupation)) you.push(t(values.occupation))
  if (values.gender && values.gender !== 'not_provided') you.push(GENDER_LABELS[values.gender])

  const phone =
    values.dialCode === DEFAULT_DIAL_CODE
      ? t(values.whatsapp)
      : `${values.dialCode} ${t(values.whatsapp)}`
  const contact = [`WhatsApp ${phone}`]
  if (t(values.email)) contact.push(t(values.email).toLowerCase())
  if (values.instagram) contact.push(`@${values.instagram}`)
  if (hasAddress(values)) {
    const complement = t(values.complement) ? `, ${t(values.complement)}` : ''
    const place = isBrazil(values)
      ? `${t(values.city)}/${values.state} · CEP ${t(values.postalCode)}`
      : `${t(values.city)}, ${t(values.state)} · ${t(values.postalCode)} · ${countryName(values.country)}`
    contact.push(`${t(values.street)}, ${t(values.number)}${complement} · ${t(values.district)}`)
    contact.push(place)
  } else {
    contact.push('Endereço não informado')
  }

  const groups: { step: StepId; lines: string[] }[] = [
    { step: 'you', lines: you },
    { step: 'contact', lines: contact },
  ]
  if (needsGuardian(values, today)) {
    groups.push({
      step: 'guardian',
      lines: [
        t(values.guardianName),
        `CPF ${t(values.guardianDocNumber)}`,
        `WhatsApp ${t(values.guardianWhatsapp)}`,
      ],
    })
  }
  return groups
}

// O preenchimento rápido (RN-PAC-50 a 55, T58) ------------------------------------------------------

/** O documento da etapa "Seus dados", como a confirmação pelo documento o recebe. */
export function documentValuesOf(values: RegisterValues): DocumentValues {
  return {
    docNumber: values.docNumber,
    hasNoCpf: values.hasNoCpf,
    foreignType: values.foreignType,
    foreignNumber: values.foreignNumber,
    foreignCountry: values.foreignCountry,
  }
}

/** "1991-04-12" → "12/04/1991". */
function isoToBr(iso: string | null): string {
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  return `${day}/${month}/${year}`
}

/** "+5511987654321" → o DDI da lista e o número com a máscara; DDI fora da lista fica no +55. */
export function splitWhatsapp(e164: string | null): { dialCode: string; whatsapp: string } {
  if (!e164) return { dialCode: DEFAULT_DIAL_CODE, whatsapp: '' }
  const dial = DIAL_CODES.map((d) => d.code)
    .sort((a, b) => b.length - a.length)
    .find((code) => e164.startsWith(code))
  const dialCode = dial ?? DEFAULT_DIAL_CODE
  const national = dial ? e164.slice(dial.length) : onlyDigits(e164)
  return { dialCode, whatsapp: maskWhatsapp(national, dialCode) }
}

/**
 * Os dados que a pessoa já tinha na Westhetics (RN-PAC-54) entram no formulário; o e-mail é o
 * confirmado. O que não veio fica como estava.
 */
export function applyPrefill(values: RegisterValues, prefill: Prefill): RegisterValues {
  const address = prefill.address
  return {
    ...values,
    name: prefill.name,
    birthDate: isoToBr(prefill.birthDate) || values.birthDate,
    docNumber: prefill.docNumber ? maskCpf(prefill.docNumber) : values.docNumber,
    hasNoCpf: prefill.docNumber ? false : prefill.foreignDocument ? true : values.hasNoCpf,
    foreignType: prefill.foreignDocument?.type ?? values.foreignType,
    foreignNumber: prefill.foreignDocument?.number ?? values.foreignNumber,
    foreignCountry: prefill.foreignDocument?.country ?? values.foreignCountry,
    rg: prefill.rg ?? values.rg,
    gender: prefill.gender === 'not_provided' ? values.gender : prefill.gender,
    maritalStatus: prefill.maritalStatus ?? values.maritalStatus,
    occupation: prefill.occupation ?? values.occupation,
    email: prefill.email,
    ...(prefill.whatsapp ? splitWhatsapp(prefill.whatsapp) : {}),
    ...(address
      ? {
          country: address.country,
          postalCode:
            address.country === 'BR'
              ? maskPostalCode(address.postalCode ?? '')
              : (address.postalCode ?? ''),
          street: address.street ?? '',
          number: address.number ?? '',
          complement: address.complement ?? '',
          district: address.district ?? '',
          city: address.city ?? '',
          state: address.state ?? '',
        }
      : {}),
  }
}
